import type { INodeDto } from '@/types/INodeDto';

/**
 * NodeTypes enum matching the server-side enum.
 * Used to identify Element/Component parent-child relationships.
 */
export enum NodeTypes {
  Category = 1,
  Family = 2,
  Type = 3,
  Element = 4,
  Component = 5,
  Supplemental = 6,
}

/** String enum for tree node types used in TreeRow/TreeNode */
export enum TreeNodeType {
  Group = 'group',
  Element = 'element',
  Component = 'component',
  Supplemental = 'supplemental',
}

/**
 * Extracts the parent path from a SQL Server HierarchyId string.
 * e.g., "/1/2/3/" -> "/1/2/", "/1/" -> undefined (root)
 * Used for building parent-child relationships with NodePath which is globally unique across models.
 */
function getParentNodePath(nodePath: string | null | undefined): string | undefined {
  if (!nodePath || nodePath === '/') return undefined;

  // Remove trailing slash and split
  const trimmed = nodePath.endsWith('/') ? nodePath.slice(0, -1) : nodePath;
  const parts = trimmed.split('/').filter(p => p !== '');

  if (parts.length <= 1) {
    // This is a root node (e.g., "/1/")
    return undefined;
  }

  // Remove last segment and rebuild path
  parts.pop();
  return '/' + parts.join('/') + '/';
}

/**
 * Represents a row in the flattened tree structure for MUI DataGrid.
 * Compatible with treeData=true mode using the path property.
 */
export interface TreeRow {
  /** Unique identifier for the row */
  id: string;
  /** The materialized path as an array (e.g., ['0', '1', '2']) */
  path: string[];
  /** Depth in the tree (0 = root) */
  depth: number;
  /** Parent row id, or null for root nodes */
  parentId: string | null;
  /** Type of row: grouping and node kinds */
  type: TreeNodeType;
  /** The field name used for grouping at this level (only for type='group') */
  field: string;
  /** The raw value used for grouping (only for type='group'), useful for lookups */
  rawValue?: string;
  /** Whether this is a leaf node (no children) */
  leaf: boolean;
  /** Number of siblings at this level */
  siblings: number;
  /** Label for display */
  label: string | null;
  /** The original INodeDto data (only for type='node') */
  data?: INodeDto;
  /** Child count for this node */
  childCount: number;
}

/**
 * Configuration for a grouping level.
 */
export interface GroupingField {
  /** The INodeDto field name to group by */
  field: keyof INodeDto;
  /** Optional label for the grouping level */
  label?: string;
  /** 
   * Optional function to transform the field value for grouping.
   * Can return a string (used for both id and label) or a GroupTransformResult
   * for separate id and label values.
   */
  transform?: (value: any, node: INodeDto) => string | GroupTransformResult;
}

/**
 * Result from a grouping transform that needs separate id and label.
 * Use this when the grouping id needs to be unique but the display label
 * should remain the original value.
 */
export interface GroupTransformResult {
  /** The unique id used for grouping (may include disambiguation) */
  id: string;
  /** The display label (typically the original value) */
  label: string;
}

/**
 * Function type for resolving field values to display labels.
 * Used to convert IDs (like cadModelCategoryId) to friendly names.
 * 
 * @param field The field name being grouped (e.g., 'cadModelCategoryId')
 * @param value The raw field value (e.g., a GUID)
 * @returns The display label for the value
 */
export type LabelResolver = (field: string, value: string | number | undefined) => string;

/**
 * Options for tree building behavior.
 */
export interface TreeBuilderOptions {
  /**
   * When true (default), grouping only applies to Element nodes.
   * Component nodes remain as children of their parent Elements,
   * and Supplemental nodes remain as children of their parent Elements/Components.
   * 
   * When false, all node types are treated equally during grouping.
   * Use this when applying custom GroupingFields that should apply to all nodes.
   */
  preserveHierarchy?: boolean;
}

/**
 * Internal tree node used during tree construction.
 */
interface TreeNode {
  id: string;
  type: TreeNodeType;
  label: string | null;
  /** The field name used for grouping at this level (only for type='group') */
  field?: string;
  /** The raw value used for grouping (only for type='group'), useful for lookups */
  rawValue?: string;
  data?: INodeDto;
  children: TreeNode[];
}

/** Maximum recursion depth to prevent stack overflow */
const MAX_TREE_DEPTH = 100;

/**
 * Progress callback for tree building operations.
 * @param phase - Current phase of the build process
 * @param percent - Progress percentage (0-100)
 * @param processedCount - Number of nodes processed so far
 * @param totalCount - Total number of nodes to process
 */
export type TreeBuildProgressCallback = (
  phase: 'grouping' | 'flattening',
  percent: number,
  processedCount: number,
  totalCount: number
) => void;

/**
 * Builds a tree structure from INodeDto data with dynamic grouping support.
 * Outputs rows compatible with MUI DataGrid's treeData=true mode.
 */
export class TreeBuilder {
  private nodes: readonly INodeDto[];
  private supplementalNodes: readonly INodeDto[];
  private groupingFields: GroupingField[];
  private labelResolver?: LabelResolver;
  private onProgress?: TreeBuildProgressCallback;
  private options: TreeBuilderOptions;

  /**
   * Creates a new TreeBuilder.
   * @param nodes The INodeDto array to build the tree from (typically from OPFS - Elements and Components)
   * @param groupingFields Array of fields to group by (in order from root to leaf)
   * @param labelResolver Optional function to resolve field values to display labels
   * @param onProgress Optional progress callback for tracking build progress
   * @param options Optional configuration options
   * @param supplementalNodes Optional array of supplemental INodeDto (from IndexedDB - scenario-specific)
   *                          If provided, these are used directly instead of filtering from nodes array.
   *                          This is more efficient as it avoids iterating all nodes to find supplementals.
   */
  constructor(
    nodes: readonly INodeDto[],
    groupingFields: GroupingField[],
    labelResolver?: LabelResolver,
    onProgress?: TreeBuildProgressCallback,
    options?: TreeBuilderOptions,
    supplementalNodes?: readonly INodeDto[]
  ) {
    this.nodes = nodes;
    this.supplementalNodes = supplementalNodes ?? [];
    this.groupingFields = groupingFields;
    this.labelResolver = labelResolver;
    this.onProgress = onProgress;
    this.options = { preserveHierarchy: true, ...options };
  }

  /**
   * Gets the current options.
   */
  getOptions(): TreeBuilderOptions {
    return { ...this.options };
  }

  /**
   * Sets the options.
   */
  setOptions(options: TreeBuilderOptions): void {
    this.options = { ...this.options, ...options };
  }

  /**
   * Sets the progress callback.
   */
  setProgressCallback(callback: TreeBuildProgressCallback | undefined): void {
    this.onProgress = callback;
  }

  /**
   * Sets the grouping fields dynamically.
   */
  setGroupingFields(fields: GroupingField[]): void {
    this.groupingFields = fields;
  }

  /**
   * Gets the current grouping fields.
   */
  getGroupingFields(): GroupingField[] {
    return [...this.groupingFields];
  }

  /**
   * Sets the label resolver function.
   */
  setLabelResolver(resolver: LabelResolver | undefined): void {
    this.labelResolver = resolver;
  }

  /**
   * Clears internal references to free memory.
   * Call this after building the tree when you no longer need the builder.
   */
  clear(): void {
    // Clear the nodes reference - cast to mutable to allow clearing
    (this as unknown as { nodes: INodeDto[]; }).nodes = [];
    this.groupingFields = [];
    this.labelResolver = undefined;
    this.onProgress = undefined;
    this.options = { preserveHierarchy: true };
  }

  /**
   * Builds the tree and returns flattened rows for MUI DataGrid.
   * @returns Array of TreeRow objects with materialized paths
   */
  build(): TreeRow[] {
    const totalNodes = this.nodes.length;

    // Report grouping phase start
    this.onProgress?.('grouping', 0, 0, totalNodes);

    // Build the hierarchical tree
    const rootNodes = this.buildHierarchy();

    // Sort root nodes by label
    rootNodes.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));

    // Report grouping complete
    this.onProgress?.('grouping', 100, totalNodes, totalNodes);

    // Flatten to rows with paths (passes total nodes for progress calculation)
    return this.flattenTree(rootNodes, totalNodes);
  }

  /**
   * Builds the hierarchical tree structure by grouping nodes.
   */
  private buildHierarchy(): TreeNode[] {
    if (this.groupingFields.length === 0) {
      // No grouping - still apply Element-Component hierarchy
      return this.createLeafNodesWithComponentHierarchy(this.nodes);
    }

    if (this.options.preserveHierarchy) {
      // Only group Element nodes, preserving Component/Supplemental as children
      return this.groupElementsWithHierarchy(this.nodes);
    }

    // Group all nodes recursively (treats all node types equally)
    return this.groupNodes(this.nodes, 0, '');
  }

  /**
   * Groups only Element nodes while preserving Component/Supplemental hierarchy.
   * Components remain as children of their parent Elements.
   * Supplementals remain as children of their parent Elements/Components.
   * Uses NodePath (SQL Server HierarchyId) for globally unique parent-child relationships.
   * 
   * Optimization: If supplementalNodes were passed to constructor, they are used directly
   * instead of filtering from the main nodes array. This avoids iterating all nodes to find
   * supplementals (which are typically scenario-specific and stored separately in IndexedDB).
   */
  private groupElementsWithHierarchy(nodes: readonly INodeDto[]): TreeNode[] {
    // Separate elements and components from the main nodes array (from OPFS)
    // Use Maps to deduplicate by nodeId (prevents duplicate path errors in MUI DataGrid)
    const elementsById = new Map<bigint, INodeDto>();
    const componentsById = new Map<bigint, INodeDto>();

    for (const node of nodes) {
      const nid = node.nodeId!;
      const bigId = typeof nid === "bigint" ? nid : BigInt(nid);
      if (node.nodeType === NodeTypes.Element) {
        if (elementsById.has(bigId)) {
          console.warn(`[TreeBuilder] Duplicate element nodeId ${nid} detected, keeping first occurrence`);
        } else {
          elementsById.set(bigId, node);
        }
      } else if (node.nodeType === NodeTypes.Component) {
        if (componentsById.has(bigId)) {
          console.warn(`[TreeBuilder] Duplicate component nodeId ${nid} detected, keeping first occurrence`);
        } else {
          componentsById.set(bigId, node);
        }
      }
      // Note: Supplementals are NOT extracted from main nodes anymore.
      // They come from this.supplementalNodes (passed to constructor from IndexedDB)
    }

    const elements = Array.from(elementsById.values());
    const components = Array.from(componentsById.values());

    // Use supplemental nodes passed to constructor (from IndexedDB)
    // This is more efficient than filtering from the main nodes array
    const supplementals = this.supplementalNodes;

    // Build lookup maps for children using NodePath
    // Components are children of Elements, Supplementals are children of Elements/Components
    const componentsByParentPath = new Map<string, INodeDto[]>();
    for (const component of components) {
      const parentPath = getParentNodePath(component.nodePath);
      if (parentPath) {
        const existing = componentsByParentPath.get(parentPath) || [];
        existing.push(component);
        componentsByParentPath.set(parentPath, existing);
      }
    }

    const supplementalsByParentPath = new Map<string, INodeDto[]>();
    for (const supplemental of supplementals) {
      const parentPath = getParentNodePath(supplemental.nodePath);
      if (parentPath) {
        const existing = supplementalsByParentPath.get(parentPath) || [];
        existing.push(supplemental);
        supplementalsByParentPath.set(parentPath, existing);
      }
    }

    // Helper to get supplemental children for a node by its NodePath
    const getSupplementalChildren = (nodePath: string | null | undefined): TreeNode[] => {
      if (!nodePath) return [];
      const childSupplementals = supplementalsByParentPath.get(nodePath) || [];
      return childSupplementals.map((supp) => ({
        id: supp.nodeId!.toString(),
        type: TreeNodeType.Supplemental,
        label: supp.cadName ?? null,
        data: supp,
        children: getSupplementalChildren(supp.nodePath),
      }));
    };

    // Helper to build children for an element using NodePath
    const buildElementChildren = (element: INodeDto): TreeNode[] => {
      const childComponents = element.nodePath ? componentsByParentPath.get(element.nodePath) || [] : [];
      return [
        ...childComponents.map((comp) => ({
          id: comp.nodeId!.toString(),
          type: TreeNodeType.Component,
          label: comp.cadName ?? null,
          data: comp,
          children: getSupplementalChildren(comp.nodePath),
        })),
        ...getSupplementalChildren(element.nodePath),
      ];
    };

    // Group only the elements
    return this.groupNodesWithChildBuilder(elements, 0, '', buildElementChildren);
  }

  /**
   * Recursively groups nodes by the current grouping field, using a child builder function
   * to create children for leaf nodes instead of recursing further.
   */
  private groupNodesWithChildBuilder(
    nodes: INodeDto[],
    fieldIndex: number,
    parentPath: string,
    buildChildren: (node: INodeDto) => TreeNode[]
  ): TreeNode[] {
    if (fieldIndex >= this.groupingFields.length) {
      // No more grouping levels - create element nodes with their children
      return nodes.map((element) => ({
        id: element.nodeId!.toString(),
        type: TreeNodeType.Element,
        label: element.cadName ?? null,
        data: element,
        children: buildChildren(element),
      }));
    }

    const groupingField = this.groupingFields[fieldIndex];
    const { groups, labels } = this.groupBy(nodes, groupingField);

    const result: TreeNode[] = [];
    let groupIndex = 0;

    for (const [groupId, groupNodes] of Object.entries(groups)) {
      // Prefix root-level group paths with "g:" to avoid collision with numeric nodeIds
      const groupPath = parentPath ? `${parentPath}/${groupIndex}` : `g:${groupIndex}`;

      // Recursively group children
      const children = this.groupNodesWithChildBuilder(
        groupNodes as INodeDto[],
        fieldIndex + 1,
        groupPath,
        buildChildren
      );

      // Sort children by label
      children.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));

      // Only skip creating group node for single leaf child at the LAST grouping level (e.g., Type)
      // Always create group nodes for earlier levels (e.g., Category, Family) to preserve hierarchy
      const isLastGroupingLevel = fieldIndex === this.groupingFields.length - 1;
      if (isLastGroupingLevel && children.length === 1 && children[0].type !== TreeNodeType.Group) {
        result.push(children[0]);
        groupIndex++;
        continue;
      }

      // Use tracked label if available, otherwise resolve from id
      const displayLabel = labels.get(groupId);
      const label = displayLabel ?? this.resolveLabel(groupingField.field as string, groupId);

      // Create group node
      const groupNode: TreeNode = {
        id: groupPath,
        type: TreeNodeType.Group,
        label,
        field: groupingField.field as string,
        rawValue: groupId,
        children,
      };

      result.push(groupNode);
      groupIndex++;
    }

    return result;
  }

  /**
   * Recursively groups nodes by the current grouping field.
   */
  private groupNodes(nodes: readonly INodeDto[], fieldIndex: number, parentPath: string): TreeNode[] {
    if (fieldIndex >= this.groupingFields.length) {
      // No more grouping levels - create leaf nodes with Element-Component hierarchy
      return this.createLeafNodesWithComponentHierarchy(nodes);
    }

    const groupingField = this.groupingFields[fieldIndex];
    const { groups, labels } = this.groupBy(nodes, groupingField);

    const result: TreeNode[] = [];
    let groupIndex = 0;

    for (const [groupId, groupNodes] of Object.entries(groups)) {
      // Prefix root-level group paths with "g:" to avoid collision with numeric nodeIds
      const groupPath = parentPath ? `${parentPath}/${groupIndex}` : `g:${groupIndex}`;

      // Recursively group children
      const children = this.groupNodes(groupNodes as INodeDto[], fieldIndex + 1, groupPath);

      // Sort children by label
      children.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));

      // Only skip creating group node for single leaf child at the LAST grouping level (e.g., Type)
      // Always create group nodes for earlier levels (e.g., Category, Family) to preserve hierarchy
      const isLastGroupingLevel = fieldIndex === this.groupingFields.length - 1;
      if (isLastGroupingLevel && children.length === 1 && children[0].type !== TreeNodeType.Group) {
        result.push(children[0]);
        groupIndex++;
        continue;
      }

      // Use tracked label if available, otherwise resolve from id
      const displayLabel = labels.get(groupId);
      const label = displayLabel ?? this.resolveLabel(groupingField.field as string, groupId);

      // Create group node
      const groupNode: TreeNode = {
        id: groupPath,
        type: TreeNodeType.Group,
        label,
        field: groupingField.field as string,
        rawValue: groupId,
        children,
      };

      result.push(groupNode);
      groupIndex++;
    }

    return result;
  }

  /**
   * Resolves a field value to a display label.
   */
  private resolveLabel(field: string, value: string): string {
    if (!value) return '(Empty)';

    // Use the label resolver if provided
    if (this.labelResolver) {
      const resolved = this.labelResolver(field, value);
      return resolved || value || '(Empty)';
    }

    return value;
  }

  /**
   * Creates leaf tree nodes with proper Element-Component-Supplemental hierarchy.
   * Components (nodeType=5) become children of Elements (nodeType=4) that share the same cadId.
   * Supplementals (nodeType=6) become children of their parent Element/Component via nodePath hierarchy.
   * 
   * An element is NOT a leaf node if it has child components.
   * An element or component is NOT a leaf node if it has child supplementals.
   * 
   * Optimization: If supplementalNodes were passed to constructor, they are used directly
   * instead of filtering from the nodes array.
   */
  private createLeafNodesWithComponentHierarchy(nodes: readonly INodeDto[]): TreeNode[] {
    // Separate Elements, Components, and other node types from the passed nodes
    // Use Maps to deduplicate by nodeId (prevents duplicate path errors in MUI DataGrid)
    const elementsById = new Map<bigint, INodeDto>();
    const componentsById = new Map<bigint, INodeDto>();
    const otherNodesById = new Map<bigint, INodeDto>();

    for (const node of nodes) {
      const nid = node.nodeId!;
      const bigId = typeof nid === "bigint" ? nid : BigInt(nid);
      if (node.nodeType === NodeTypes.Element) {
        if (!elementsById.has(bigId)) {
          elementsById.set(bigId, node);
        }
      } else if (node.nodeType === NodeTypes.Component) {
        if (!componentsById.has(bigId)) {
          componentsById.set(bigId, node);
        }
      } else if (node.nodeType !== NodeTypes.Supplemental) {
        // Skip supplementals here - they come from this.supplementalNodes
        if (!otherNodesById.has(bigId)) {
          otherNodesById.set(bigId, node);
        }
      }
    }

    const elements = Array.from(elementsById.values());
    const components = Array.from(componentsById.values());
    const otherNodes = Array.from(otherNodesById.values());

    // Use supplemental nodes passed to constructor (from IndexedDB) when available
    // This is more efficient than filtering from the main nodes array
    const supplementals = this.supplementalNodes;

    // Build a map of cadId -> Element for quick lookup
    const elementByCadId = new Map<string, INodeDto>();
    for (const element of elements) {
      if (element.cadId) {
        elementByCadId.set(element.cadId, element);
      }
    }

    // Group Components by their parent Element's cadId
    const componentsByElementCadId = new Map<string, INodeDto[]>();
    const orphanComponents: INodeDto[] = [];

    for (const component of components) {
      if (component.cadId && elementByCadId.has(component.cadId)) {
        const existing = componentsByElementCadId.get(component.cadId) || [];
        existing.push(component);
        componentsByElementCadId.set(component.cadId, existing);
      } else {
        // Component without a matching Element - treat as standalone
        orphanComponents.push(component);
      }
    }

    // Group Supplementals by their parent's nodePath (using HierarchyId)
    // This correctly associates supplementals with their parent Elements/Components
    const supplementalsByParentPath = new Map<string, INodeDto[]>();
    const orphanSupplementals: INodeDto[] = [];

    for (const supplemental of supplementals) {
      const parentPath = getParentNodePath(supplemental.nodePath);
      if (parentPath) {
        const existing = supplementalsByParentPath.get(parentPath) || [];
        existing.push(supplemental);
        supplementalsByParentPath.set(parentPath, existing);
      } else {
        // Supplemental without a parent path - treat as standalone
        orphanSupplementals.push(supplemental);
      }
    }

    // Helper to get supplemental children for a node by its nodePath
    const getSupplementalChildren = (nodePath: string | null | undefined): TreeNode[] => {
      if (!nodePath) return [];
      const childSupplementals = supplementalsByParentPath.get(nodePath) || [];
      return childSupplementals.map((supp) => ({
        id: supp.nodeId!.toString(),
        type: TreeNodeType.Supplemental,
        label: supp.cadName ?? null,
        data: supp,
        children: getSupplementalChildren(supp.nodePath), // Recursively get nested supplementals
      }));
    };

    // Create tree nodes
    const result: TreeNode[] = [];

    // Create Element nodes with their Component and Supplemental children
    // Elements with child components are NOT leaf nodes
    // Elements with child supplementals are NOT leaf nodes
    for (const element of elements) {
      const childComponents = element.cadId ? componentsByElementCadId.get(element.cadId) || [] : [];
      const directSupplementals = getSupplementalChildren(element.nodePath);

      const elementNode: TreeNode = {
        id: element.nodeId!.toString(),
        type: TreeNodeType.Element,
        label: element.cadName ?? null,
        data: element,
        children: [
          // Component children with their supplemental children
          // Components with child supplementals are NOT leaf nodes
          ...childComponents.map((comp) => ({
            id: comp.nodeId!.toString(),
            type: TreeNodeType.Component,
            label: comp.cadName ?? null,
            data: comp,
            children: getSupplementalChildren(comp.nodePath),
          })),
          // Direct supplemental children of the element
          ...directSupplementals,
        ],
      };

      result.push(elementNode);
    }

    // Add orphan Components as standalone (shouldn't happen normally)
    for (const comp of orphanComponents) {
      result.push({
        id: comp.nodeId!.toString(),
        type: TreeNodeType.Component,
        label: comp.cadName ?? null,
        data: comp,
        children: getSupplementalChildren(comp.nodePath),
      });
    }

    // Add orphan Supplementals as standalone
    for (const supp of orphanSupplementals) {
      result.push({
        id: supp.nodeId!.toString(),
        type: TreeNodeType.Supplemental,
        label: supp.cadName ?? null,
        data: supp,
        children: getSupplementalChildren(supp.nodePath),
      });
    }

    return result;
  }

  /**
   * Result of grouping operation with separate groups and labels.
   */
  private groupBy(nodes: readonly INodeDto[], field: GroupingField): { groups: Record<string, INodeDto[]>; labels: Map<string, string>; } {
    const groups: Record<string, INodeDto[]> = {};
    const labels = new Map<string, string>();

    for (const node of nodes) {
      const rawValue = node[field.field];
      let id: string;
      let displayLabel: string | undefined;

      // Apply transform if provided
      if (field.transform) {
        const transformed = field.transform(rawValue, node);
        if (typeof transformed === 'object' && transformed !== null && 'id' in transformed) {
          // Transform returned separate id and label
          id = transformed.id;
          displayLabel = transformed.label;
        } else {
          // Transform returned a string - use for both id and label
          id = this.toGroupId(transformed);
        }
      } else {
        id = this.toGroupId(rawValue);
      }

      if (!groups[id]) {
        groups[id] = [];
        // Store the display label (only need to do this once per id)
        if (displayLabel !== undefined) {
          labels.set(id, displayLabel);
        }
      }
      groups[id].push(node);
    }

    return { groups, labels };
  }

  /**
   * Converts a value to a string id for grouping.
   */
  private toGroupId(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'bigint') {
      return value.toString();
    }
    return String(value);
  }

  /**
   * Flattens the tree to an array of TreeRow objects with materialized paths.
   * Uses iterative approach to avoid stack overflow with deep/circular trees.
   * @param totalNodesEstimate - Estimated total nodes for progress calculation
   */
  private flattenTree(rootNodes: TreeNode[], totalNodesEstimate: number = 0): TreeRow[] {
    const rows: TreeRow[] = [];

    // Use a stack for iterative traversal instead of recursion
    // Each item: [nodes array, parent info]
    interface StackItem {
      nodes: TreeNode[];
      nodeIndex: number;
      parentId: string | null;
      depth: number;
      path: string[];
    }

    const stack: StackItem[] = [{
      nodes: rootNodes,
      nodeIndex: 0,
      parentId: null,
      depth: 0,
      path: [],
    }];

    // Track visited TreeNode objects to detect circular references
    const visitedObjects = new WeakSet<TreeNode>();
    let processedCount = 0;
    const maxProcessedNodes = 1000000; // Safety limit
    let lastReportedPercent = -1;

    // Report flattening start
    this.onProgress?.('flattening', 0, 0, totalNodesEstimate);

    while (stack.length > 0) {
      const current = stack[stack.length - 1];

      // Safety check: prevent infinite loops
      if (processedCount > maxProcessedNodes) {
        console.error(`TreeBuilder: Exceeded maximum node limit (${maxProcessedNodes}). Possible circular reference or extremely large tree.`);
        break;
      }

      // Safety check: prevent extremely deep trees
      if (current.depth > MAX_TREE_DEPTH) {
        console.warn(`TreeBuilder: Maximum tree depth (${MAX_TREE_DEPTH}) exceeded at depth ${current.depth}.`);
        stack.pop();
        continue;
      }

      // Check if we've processed all nodes at this level
      if (current.nodeIndex >= current.nodes.length) {
        stack.pop();
        continue;
      }

      const node = current.nodes[current.nodeIndex];
      current.nodeIndex++;
      processedCount++;

      // Report progress every 1% (throttle updates)
      if (this.onProgress && totalNodesEstimate > 0) {
        const percent = Math.floor((processedCount / totalNodesEstimate) * 100);
        if (percent > lastReportedPercent) {
          lastReportedPercent = percent;
          this.onProgress('flattening', Math.min(percent, 99), processedCount, totalNodesEstimate);
        }
      }

      // Cycle detection: check if we've already visited this exact TreeNode object
      if (visitedObjects.has(node)) {
        console.warn(`TreeBuilder: Circular reference detected for node "${node.id}" (same object reference). Skipping.`);
        continue;
      }
      visitedObjects.add(node);

      const id = node.id;
      const currentPath = [...current.path, id];

      const row: TreeRow = {
        id: id,
        path: currentPath,
        depth: current.depth,
        parentId: current.parentId,
        type: node.type,
        field: node.field ?? '',
        leaf: node.children.length === 0,
        siblings: current.nodes.length,
        label: node.label,
        childCount: node.children.length,
      };

      // Add rawValue for group nodes (useful for lookups)
      if (node.type === TreeNodeType.Group && node.rawValue) {
        row.rawValue = node.rawValue;
      }

      // Add nodeData for node/element/component types (not groups)
      if (node.type !== TreeNodeType.Group && node.data) {
        row.data = node.data;
      }

      rows.push(row);

      // Add children to stack for processing (if any)
      if (node.children.length > 0) {
        stack.push({
          nodes: node.children,
          nodeIndex: 0,
          parentId: id,
          depth: current.depth + 1,
          path: currentPath,
        });
      }
    }

    // Report flattening complete
    this.onProgress?.('flattening', 100, processedCount, totalNodesEstimate);

    return rows;
  }

  /**
   * Creates default grouping fields similar to updateAssignNodes.
   * Groups by: cadModelCategoryId -> cadFamily -> cadName
   */
  static defaultGroupingFields(): GroupingField[] {
    return [
      { field: 'cadModelCategoryId', label: 'Category' },
      {
        field: 'cadFamily',
        label: 'Family',
        // Handle edge case where cadFamily equals cadName by creating a unique id
        // but preserving the original value as the display label
        transform: (value, node): string | GroupTransformResult => {
          const displayValue = value ?? '';
          if (value === node.cadName) {
            // Create unique id but keep original label
            return {
              id: `${node.cadModelCategoryId}/${value}`,
              label: displayValue || '(Empty)',
            };
          }
          return displayValue;
        }
      },
      { field: 'cadName', label: 'Type' },
    ];
  }

  /**
   * Creates a TreeBuilder with default grouping (Category -> Family -> Type).
   * @param nodes The INodeDto array to build the tree from
   * @param labelResolver Optional function to resolve field values to display labels
   * @param onProgress Optional progress callback
   * @param options Optional configuration options
   */
  static withDefaultGrouping(
    nodes: readonly INodeDto[],
    labelResolver?: LabelResolver,
    onProgress?: TreeBuildProgressCallback,
    options?: TreeBuilderOptions
  ): TreeBuilder {
    return new TreeBuilder(nodes, TreeBuilder.defaultGroupingFields(), labelResolver, onProgress, options);
  }
}
